import { useEffect, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import { THREAD_REPORTS, type ThreadReportKey, type ThreadReportRequest } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { extractApiErrorMessage } from '../../utils/apiError';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useTimelines } from '../../hooks/useEventOps';
import { useFloorPlans } from '../../hooks/useFloorPlans';

/**
 * Choosing which reports to send (COMMUNICATIONS-DESIGN H4).
 *
 * ⚠️ **The list is the shared registry, not a list written here.**
 * `THREAD_REPORTS` is what the endpoint validates against too, so a report
 * added there appears here and is accepted there without either side being
 * edited — and a checkbox can never offer something the server will refuse.
 *
 * ⚠️ **`selects` is what makes the second list appear.** An event has one
 * expense ledger and so one Summary; it may have three timelines and four floor
 * plans, and "the timeline" is not one document. A report that names a
 * collection grows a multi-select beneath it, and the choice is the report's
 * property rather than a case this dialog knows about.
 *
 * ⚠️ **Nothing chosen means every one, and the copy says so.** That is what the
 * existing `/export` endpoints have always done, and it is the right answer to
 * "send me the timeline" on an event with exactly one. Forcing a choice would
 * be a required field with one option in it.
 */
export interface ThreadReportModalProps {
  open: boolean;
  onClose: () => void;
  /** The event the reports are about. */
  eventId: string;
  onAttach: (reports: ThreadReportRequest[]) => Promise<void>;
}

export function ThreadReportModal({ open, onClose, eventId, onAttach }: ThreadReportModalProps) {
  const [chosen, setChosen] = useState<ThreadReportKey[]>([]);
  const [ids, setIds] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsTimelines = chosen.includes('timeline');
  const needsFloorPlans = chosen.includes('floor-plan');

  /*
   * ⚠️ **Fetched only once the report is ticked.** A dialog that loaded every
   * timeline and every floor plan on open would pay for two requests each time
   * somebody wanted the vendor list — and would pay them again on every
   * conversation.
   */
  const { data: timelines = [] } = useTimelines(needsTimelines ? eventId : undefined);
  const { data: floorPlans = [] } = useFloorPlans(needsFloorPlans ? { eventId } : undefined);

  useEffect(() => {
    if (!open) return;
    setChosen([]);
    setIds({});
    setError(null);
    setBusy(false);
  }, [open]);

  const optionsFor = useMemo(
    () => ({
      timeline: timelines.map((timeline) => ({
        id: timeline.id,
        name: timeline.title || 'Timeline',
      })),
      floorPlan: floorPlans.map((plan) => ({ id: plan.id, name: plan.name })),
    }),
    [timelines, floorPlans],
  );

  function toggle(key: ThreadReportKey) {
    setChosen((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  async function handleAttach() {
    setError(null);
    setBusy(true);
    try {
      await onAttach(
        chosen.map((key) => ({
          key,
          // Only sent when something was picked: an empty array and an absent
          // one mean the same thing to the server, and sending the empty one
          // would be the dialog asserting a choice nobody made.
          ...(ids[key]?.length ? { ids: ids[key] } : {}),
        })),
      );
      onClose();
    } catch (err) {
      /*
       * ⚠️ **The server's message, not Axios's.** An `AxiosError` *is* an
       * `Error`, and its message is "Request failed with status code 500" — so
       * reading `err.message` replaced every reason the endpoint gave ("Floor
       * plan storage refused…", "This conversation is not about an event") with
       * a status code, on the one screen where the reason is the whole point.
       */
      setError(
        extractApiErrorMessage(
          err,
          err instanceof Error ? err.message : 'The reports could not be attached.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Attach a report"
      size="md"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleAttach()}
            disabled={chosen.length === 0 || busy}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="attach-reports"
          >
            {busy ? 'Preparing…' : 'Attach as PDF'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        {error && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {THREAD_REPORTS.map((report) => {
          const ticked = chosen.includes(report.key);
          const list = report.selects ? optionsFor[report.selects] : null;
          return (
            <div key={report.key} className="border border-gray-200 rounded-lg p-3">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={ticked}
                  onChange={() => toggle(report.key)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  data-testid={`report-${report.key}`}
                />
                <span>
                  <span className="flex items-center gap-1.5 font-medium">
                    <FileText size={13} />
                    {report.label}
                  </span>
                  <span className="text-xs text-gray-500">{report.description}</span>
                </span>
              </label>

              {ticked && list !== null && (
                <div className="mt-2 pl-6">
                  <SearchableListSelect
                    items={list}
                    value={ids[report.key] ?? []}
                    onChange={(value) =>
                      setIds((prev) => ({
                        ...prev,
                        [report.key]: Array.isArray(value) ? value : [value],
                      }))
                    }
                    getValue={(option) => option.id}
                    getLabel={(option) => option.name}
                    placeholder="All of them"
                    ariaLabel={`Which ${report.label.toLowerCase()}`}
                    multiple
                    emptyMessage="This event has none yet."
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </EntityModal>
  );
}
